/* @flow */
import { stringify } from 'querystring';
import { Map, Iterable, fromJS as toImmutable, List } from 'immutable';
import curry from 'ramda/src/curry';
import contains from 'ramda/src/contains';
import ifElse from 'ramda/src/ifElse';
import map from 'ramda/src/map';
import trim from 'ramda/src/trim';
import compose from 'ramda/src/compose';
import concat from 'ramda/src/concat';
import head from 'ramda/src/head';
import tail from 'ramda/src/tail';
import always from 'ramda/src/always';
import prop from 'ramda/src/prop';
import defaultTo from 'ramda/src/defaultTo';
import pick from 'ramda/src/pick';
import pipe from 'ramda/src/pipe';
import path from 'ramda/src/path';
import { saveAs } from 'file-saver';
import bowser from 'bowser';

import { IO } from '../fp';

/**
 * We could import this from ramda... but it's such a straightforward function.
 */
const identity = x => x;

const Base64 = {
  to: x => window.btoa(x),
  from: x => window.atob(x),
};

// I created these partly for semantic reasons and partially because they are
// dangerous. At least atob can throw if called on a malformed string.
export const toBase64 = compose(map(compose(trim, Base64.to, encodeURIComponent)), IO.of);
export const fromBase64 = compose(map(compose(trim, decodeURIComponent, Base64.from)), IO.of);

// Encode an id to Base 64 to be a valid URL
export const encodeId = (id: string): string => {
  const encode = ifElse(
    contains('|'),
    (id) => toBase64(id).foldOr(err => {
      console.warn(`Could not encode ID - "${id}"`, err);
      return '#';
    }),
    identity
  );
  return encode(id);
};

/**
 * Given an object or immutable keyed collection return the standard set of
 * params used when querying endpoints. Many of our stores hold an Options
 * record that contains both query params and also display params that are never
 * meant to be sent to the server. This function was created to standardize
 * getting the subset of params that are meant for the server.
 */
export const getQueryParams = pipe(
  defaultTo({}),
  x => typeof x.toJS === 'function' ? x.toJS() : x,
  pick(['query', 'sortBy', 'sortOrder', 'startPage', 'pageSize', 'companyId', 'companyGuid', 'simpleFetch', 'pageNumber'])
);

/**
 *
 * All indicator URLs should be base64 encoded, but in order to provide
 * backwards compat for any URLs our customers have bookmarked before this
 * change.
 *
 * Will fold left on unsuccessful decode. I.e.
 * decodeIndicatorId('a-b').fold(() => 'i will be called', () => 'i will not')
 */
export const decodeIndicatorId = (id: string) => {
  return IO.of(id)
    .map(decodeURIComponent) // This is the legacy support
    .chain(x => x.includes('|') ? IO.of(x) : fromBase64(id)) // If found pipe, assume successful decode
    .map(x => {
      if (!x.includes('|')) {
        throw new Error(`"${id}" does not appear to be a valid encoded indicator id`);
      } else {
        return x;
      }
    });
};

/**
 * NOTE: We currently have a number of util files floating around. We will
 * eventually move more and more of those utils to this file so that they can be
 * imported simply from any file.
 */

/**
 * Less error-prone versions of toLower and toUpper.
 */
export const toLower = (x: any, notDefinedValue: any = '') =>
  IO.of(x).map(x => x.toLowerCase())
    .fold(always(notDefinedValue), identity);

export const toUpper = (x: any, notDefinedValue: any = '') =>
  IO.of(x).map(x => x.toUpperCase())
    .fold(always(notDefinedValue), identity);

export const capitalize = (str: string) =>
  concat(toUpper(head(str)), tail(str));


export const isImmutable = (x: any): boolean => Iterable.isIterable(x);

/**
 * Alphabetically sort two Immutable Objects based on some key
 */
export const alphabetizeByKey = (key: string) => (a: Map<any, any>, b: Map<any, any>): number => {
  if (!Iterable.isIterable(a) || !Iterable.isIterable(b)) return 0;
  const firstName = toLower(a.get(key));
  const secondName = toLower(b.get(key));
  if (firstName < secondName) return -1;
  if (firstName > secondName) return 1;
  return 0;
};

/**
 * NOTE: This is exported as alphabetize to maintain compatibility with parts of
 * the codebase that rely on this.
 */
export const alphabetize = alphabetizeByKey('name');

export const querystring = curry((obj: Object, url: string): string => {
  return `${url}?${stringify(obj)}`;
});

type FromJS = (riviver: (k: string, v: any) => any, x: any) => any;
export const fromJS: FromJS = curry((reviver, x) => {
  return toImmutable(x, reviver);
});

/**
 * Match the behavior of lodash's `isObject`. Note that many things are
 * considered objects by the user of typeof, so if you want something of the
 * form `{}` then use isPlainObject below
 */
export const isObject = (x: any): boolean => {
  return (x != null) && (typeof x === 'object' || typeof x === 'function');
};

/**
 * Determine of something is a plain object
 */
export const isPlainObj = (x: any): boolean => {
  return !!x && (x.constructor === Object || x.constructor === undefined);
};

export const isMapLike = (x: any): boolean => {
  return isPlainObj(x) || Iterable.isKeyed(x);
};

export const isListLike = (x: any): boolean => {
  return Array.isArray(x) || Iterable.isIndexed(x);
};

/**
 * Given a string of text save it to a file on the users computer.
 */
export const saveTextToFile = (filename: string, text: string): void => {
  const blob = new Blob([text], {
    type: bowser.safari ? 'application/octet-stream' : 'text/plain;charset=utf-8',
  });
  return saveAs(blob, filename);
};

export const getterFromKeyPath = (keyPath: string[], notFoundValue: any) =>
  (state: Map<string, any>) => state.getIn(keyPath, notFoundValue);


/**
 * Like a standad pick, but it works on immutable maps. Give an array of keys,
 * and it will return an object which is a subset of the original argument
 * containing only the keys you specify.
 */
export const pickImmutable = curry((keys: string[], map: Map<any, any>) => {
  return keys.reduce((agg, k) => agg.set(k, map.get(k)), Map());
});

export const normalizeType = (type: string) =>
  IO.of(type)
    .map(toLower)
    .foldOr(() => 'undefined');


type TypedObject = {
  type: string,
  system?: string,
};

export const getType = (d: TypedObject) =>
  IO.of(d)
    .map(prop('type'))
    .map(normalizeType)
    .foldOr(() => 'undefined');

export const getPriorityLevel = (d: TypedObject) =>
  IO.of(d)
    .map(prop('priorityLevel'))
    .map(toLower)
    .foldOr(() => 'undefined');

/**
 * A quick helper to grab the csrf token from the cookie.
 */
export const getCsrfToken = () => parseCsrfToken(document.cookie);

/**
 * Parse CSRF token from a cookie string.
 */
export const parseCsrfToken = (cookies: string): ?string => {
  const cookiesMap = cookies
    .split(';')
    .map(x => x.trim().split('='))
    .reduce((a, b) => {
      a[b[0]] = b[1];
      return a;
    }, {});
  return cookiesMap['XSRF-TOKEN'];
};

export const listToOrderedMap = (idKey: string) => (list: Array<*> | List<*>) => {
  return List(list)
    .toOrderedMap()
    .mapKeys((k, v) => v[idKey]);
};

/**
 * A helper for using functional state updates in react. I.e. state => newState.
 * This function allows composing multiple state update functions, much like
 * pipe or compose from ramda.
 *
 * TODO: Would it make sense to allow this to handle plain objects as well?
 */
type StateHandler = (state: Object, props?: Object) => Object;
export const mergeState = (...fns: Array<StateHandler>) => (state: Object, props: Object) => {
  const result = {};

  // Could do this with reduce, but this seemed more readable to me. It's fine
  // that we mutate result since we created it here
  fns.forEach(fn => {
    Object.assign(result, fn(state, props));
  });

  return result;
};

/**
 * A getter for objects AND immutable collections
 *
 * Second argument is optional and mimics the behavior of Immutable collections:
 * I the key is not found then the notSetValue will be returned instead. This is
 * an important distinction, since if the key is present and the value is
 * undefined then undefined will still be returned regardless of notSetValue.
 */
export const get = (key: string, notSetValue: any = undefined) => (collection: Object) => {
  if (isImmutable(collection)) {
    return collection.get(key, notSetValue);
  } else {
    return collection.hasOwnProperty(key) ? collection[key] : notSetValue;
  }
};

/**
 * Helper to parse a error and extract the server created error message where appropriate. Otherwise,
 * returns a default or alternative error message.
 */
export const parseServerErrorMessage = (error: Object, alternative: string = 'An error occurred.'): string => {
  let message;

  if (!error) return alternative;

  const { status } = error;

  if (status && status >= 400 && status <= 500) {
    message = path(['xhr', 'response', 'message'], error);
  }

  return message || alternative;
};



// WEBPACK FOOTER //
// ./web_modules/trustar/utils/index.js